import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { EvalRun, EvalDataset, PerformanceSummary } from '../../types';
import { stepTypeFamily } from '../../types/settings';
import type { PromptType } from '../../types/settings';
import './EvalRuns.css';
import { TabStrip } from '@shared-ui/components';

type TabType = 'runs' | 'performance';

interface Prompt {
  id: string;
  name: string;
  version: number;
  type: string;
}

interface EvalRunsProps {
  /** When set, locks to this tab and hides internal header/tabs */
  forceTab?: TabType;
}

export default function EvalRuns({ forceTab }: EvalRunsProps = {}) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>(forceTab || 'performance');
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // New run modal state
  const [showNewRunModal, setShowNewRunModal] = useState(false);
  const [newRunDatasetId, setNewRunDatasetId] = useState('');
  const [newRunPromptId, setNewRunPromptId] = useState('');
  const [newRunProvider, setNewRunProvider] = useState('claude-sonnet-4-5');
  const [newRunModel, setNewRunModel] = useState('claude-sonnet-4-5');
  const [runError, setRunError] = useState('');

  // Fetch runs - always fetch, not just when tab is active
  const { data: runsData, isLoading: runsLoading, refetch: refetchRuns } = useQuery({
    queryKey: ['evalRuns', selectedDatasetId, statusFilter],
    queryFn: () => adminApi.getEvalRuns({
      datasetId: selectedDatasetId || undefined,
      status: statusFilter || undefined,
    }),
  });

  // Fetch prompts to map IDs to names
  const { data: allPromptsData } = useQuery({
    queryKey: ['allPrompts'],
    queryFn: () => adminApi.getPrompts(),
  });

  // Create prompt name lookup map
  const promptNameMap = new Map<string, string>();
  allPromptsData?.forEach((p: Prompt) => {
    promptNameMap.set(p.id, p.name);
  });

  // Fetch performance summary
  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['evalPerformance'],
    queryFn: () => adminApi.getEvalPerformanceSummary(),
    enabled: (forceTab || activeTab) === 'performance',
  });

  // Fetch datasets for filter
  const { data: datasetsData } = useQuery({
    queryKey: ['evalDatasets'],
    queryFn: () => adminApi.getEvalDatasets(),
  });

  // Fetch prompts for new run modal
  const { data: promptsData } = useQuery({
    queryKey: ['prompts'],
    queryFn: () => adminApi.getPrompts(),
    enabled: showNewRunModal,
  });

  // Create eval run mutation
  const createRunMutation = useMutation({
    mutationFn: (params: {
      datasetId: string;
      parameters: {
        promptId: string;
        promptVersion: number;
        provider: string;
        model: string;
        temperature: number;
      };
    }) => adminApi.createEvalRun(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalRuns'] });
      queryClient.invalidateQueries({ queryKey: ['evalPerformance'] });
    },
    onError: (error) => {
      console.error('Eval run error:', error);
    },
  });

  const resetNewRunForm = () => {
    setNewRunDatasetId('');
    setNewRunPromptId('');
    setNewRunProvider('claude-sonnet-4-5');
    setNewRunModel('claude-sonnet-4-5');
    setRunError('');
  };

  const handleStartRun = () => {
    if (!newRunDatasetId || !newRunPromptId) {
      setRunError('Please select a dataset and prompt');
      return;
    }

    const selectedPrompt = promptsData?.find((p: Prompt) => p.id === newRunPromptId);

    // Close modal immediately and switch to runs tab
    setShowNewRunModal(false);
    setActiveTab('runs');
    resetNewRunForm();

    createRunMutation.mutate({
      datasetId: newRunDatasetId,
      parameters: {
        promptId: newRunPromptId,
        promptVersion: selectedPrompt?.version || 1,
        provider: newRunProvider,
        model: newRunModel,
        temperature: 0,
      },
    });
  };

  const getStatusBadge = (status: EvalRun['status']) => {
    const colors: Record<string, string> = {
      pending: 'status-pending',
      running: 'status-running',
      completed: 'status-completed',
      failed: 'status-failed',
    };
    return <span className={`status-badge ${colors[status]}`}>{status}</span>;
  };

  const formatScore = (score: number) => {
    return (score * 100).toFixed(1) + '%';
  };

  const formatCost = (cost: number) => {
    return '$' + cost.toFixed(4);
  };

  const formatLatency = (ms: number) => {
    return (ms / 1000).toFixed(2) + 's';
  };

  const renderPerformanceTab = () => {
    if (performanceLoading) return <div className="loading">Loading performance data...</div>;
    if (!performanceData) return <div className="empty-state">No performance data available.</div>;

    const perf = performanceData as PerformanceSummary;

    if (perf.totalRuns === 0) {
      return (
        <div className="empty-state">
          <h3>No Eval Runs Yet</h3>
          <p>Run evaluations on your datasets to see performance metrics and analysis here.</p>
        </div>
      );
    }

    return (
      <div className="performance-content">
        {/* Summary Stats */}
        <div className="stat-cards stat-cards--auto">
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{perf.totalRuns}</div>
            <div className="stat-card-label">Total Runs</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{perf.totalEvaluations}</div>
            <div className="stat-card-label">Total Evaluations</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatScore(perf.avgScore)}</div>
            <div className="stat-card-label">Avg Score</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatLatency(perf.avgLatency)}</div>
            <div className="stat-card-label">Avg Prompt Latency</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatCost(perf.totalEvaluations > 0 ? perf.totalCost / perf.totalEvaluations : 0)}</div>
            <div className="stat-card-label">Avg Prompt Cost</div>
          </div>
        </div>

        {/* Top Configurations */}
        <div className="section">
          <h3>Top Performing Configurations</h3>
          {perf.topConfigurations.length === 0 ? (
            <p className="no-data">No configuration data available.</p>
          ) : (
            <table className="dt">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>Prompt</th>
                  <th>Avg Score</th>
                  <th>Avg Prompt Latency</th>
                  <th>Avg Prompt Cost</th>
                  <th>Runs</th>
                </tr>
              </thead>
              <tbody>
                {perf.topConfigurations.map((config, idx) => (
                  <tr key={idx}>
                    <td><span className="provider-badge">{config.provider}</span></td>
                    <td className="model-name">{config.model}</td>
                    <td className="prompt-name">{promptNameMap.get(config.promptId) || config.promptId.substring(0, 8) + '...'}</td>
                    <td>
                      <span className={`score-cell ${getScoreClass(config.avgScore)}`}>
                        {formatScore(config.avgScore)}
                      </span>
                    </td>
                    <td>{formatLatency(config.avgLatency)}</td>
                    <td>{formatCost(config.avgCost)}</td>
                    <td>{config.runCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Trends */}
        {perf.trends.length > 0 && (
          <div className="section">
            <h3>Score Trends (Last 30 Days)</h3>
            <div className="trends-chart">
              {perf.trends.map((trend, idx) => (
                <div key={idx} className="trend-bar-container" title={`${trend.date}: ${formatScore(trend.avgScore)}`}>
                  <div
                    className="trend-bar"
                    style={{ height: `${trend.avgScore * 100}%` }}
                  />
                  <span className="trend-label">{trend.date.substring(5)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getScoreClass = (score: number): string => {
    if (score >= 0.8) return 'score-good';
    if (score >= 0.6) return 'score-medium';
    return 'score-low';
  };

  const renderRunsTab = () => {
    if (runsLoading) return <div className="loading">Loading runs...</div>;
    if (!runsData || runsData.runs.length === 0) {
      return (
        <div className="empty-state">
          <h3>No Eval Runs</h3>
          <p>Create an evaluation run to test your prompts and models against labeled datasets.</p>
        </div>
      );
    }

    // Sort runs by startedAt descending
    const sortedRuns = [...runsData.runs].sort((a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );

    // Calculate summary stats from runs
    const completedRuns = runsData.runs.filter((r: EvalRun) => r.status === 'completed');
    const totalEvaluations = completedRuns.reduce((sum: number, r: EvalRun) => sum + r.statistics.total, 0);
    const avgScore = completedRuns.length > 0
      ? completedRuns.reduce((sum: number, r: EvalRun) => sum + r.statistics.aggregateScore.mean, 0) / completedRuns.length
      : 0;
    const totalTimeTaken = completedRuns.reduce((sum: number, r: EvalRun) => sum + r.statistics.totalLatencyMs, 0);
    const totalCost = completedRuns.reduce((sum: number, r: EvalRun) => sum + r.statistics.totalCost, 0);

    return (
      <div className="runs-content">
        {/* Summary Stats */}
        <div className="stat-cards stat-cards--auto">
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{runsData.runs.length}</div>
            <div className="stat-card-label">Total Runs</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{completedRuns.length}</div>
            <div className="stat-card-label">Completed</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{totalEvaluations}</div>
            <div className="stat-card-label">Evaluations</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatScore(avgScore)}</div>
            <div className="stat-card-label">Avg Score</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatLatency(totalTimeTaken)}</div>
            <div className="stat-card-label">Total Time</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{formatCost(totalCost)}</div>
            <div className="stat-card-label">Total Cost</div>
          </div>
        </div>

        <div className="filters-row">
          <select
            value={selectedDatasetId}
            onChange={(e) => setSelectedDatasetId(e.target.value)}
          >
            <option value="">All Datasets</option>
            {datasetsData?.datasets.map((ds: EvalDataset) => (
              <option key={ds.id} value={ds.id}>{ds.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button className="refresh-btn" onClick={() => refetchRuns()}>
            ↻ Refresh
          </button>
        </div>

        <table className="dt">
          <thead>
            <tr>
              <th>Date/Time</th>
              <th>Status</th>
              <th>Prompt</th>
              <th>Provider / Model</th>
              <th>Evaluated</th>
              <th>Score</th>
              <th>Time Taken</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {sortedRuns.map((run: EvalRun) => (
              <tr key={run.id}>
                <td>{format(new Date(run.startedAt), 'MMM d, yyyy HH:mm')}</td>
                <td>{getStatusBadge(run.status)}</td>
                <td className="prompt-name">{promptNameMap.get(run.parameters.promptId) || run.parameters.promptId.substring(0, 8) + '...'}</td>
                <td>
                  <span className="provider-badge">{run.parameters.provider}</span>
                  <span className="model-name">{run.parameters.model}</span>
                </td>
                <td>{run.statistics.successful} / {run.statistics.total}</td>
                <td>
                  {run.status === 'completed' && (
                    <span className={`score-cell ${getScoreClass(run.statistics.aggregateScore.mean)}`}>
                      {formatScore(run.statistics.aggregateScore.mean)}
                    </span>
                  )}
                  {run.status !== 'completed' && '-'}
                </td>
                <td>{run.status === 'completed' ? formatLatency(run.statistics.totalLatencyMs) : '-'}</td>
                <td>{run.status === 'completed' ? formatCost(run.statistics.totalCost) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const effectiveTab = forceTab || activeTab;
  const embedded = !!forceTab;

  return (
    <div className="eval-runs-page">
      {!embedded && (
        <>
          <div className="page-header">
            <h1>Eval Runs</h1>
            <button className="start-run-btn" onClick={() => setShowNewRunModal(true)}>
              + Start Eval Run
            </button>
          </div>

          <TabStrip
            ariaLabel="Eval views"
            active={activeTab}
            onChange={setActiveTab}
            tabs={[
              { key: 'performance', label: 'Performance Analysis' },
              { key: 'runs', label: 'Run History' },
            ] as const}
          />
        </>
      )}

      {embedded && effectiveTab === 'runs' && (
        <div className="page-header" style={{ borderBottom: 'none' }}>
          <div />
          <button className="start-run-btn" onClick={() => setShowNewRunModal(true)}>
            + Start Eval Run
          </button>
        </div>
      )}

      <div className="tab-content">
        {effectiveTab === 'performance' && renderPerformanceTab()}
        {effectiveTab === 'runs' && renderRunsTab()}
      </div>

      {/* New Run Modal */}
      {showNewRunModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start Eval Run</h2>
              <button className="close-btn" onClick={() => setShowNewRunModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Run evaluation on a dataset with specified prompt and LLM configuration.
                <br />
                <strong>Note:</strong> Max 8 items will be evaluated (synchronous limit).
              </p>

              {runError && (
                <div className="error-message">{runError}</div>
              )}

              <div className="form-group">
                <label>Dataset *</label>
                <select
                  value={newRunDatasetId}
                  onChange={(e) => setNewRunDatasetId(e.target.value)}
                >
                  <option value="">Select a dataset...</option>
                  {datasetsData?.datasets.map((ds: EvalDataset) => (
                    <option key={ds.id} value={ds.id}>
                      {ds.name} ({ds.metadata.inputCount} items)
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Prompt *</label>
                <select
                  value={newRunPromptId}
                  onChange={(e) => setNewRunPromptId(e.target.value)}
                >
                  <option value="">Select a prompt...</option>
                  {promptsData
                    ?.filter((p: Prompt) => stepTypeFamily(p.type as PromptType) === 'analyze')
                    .map((p: Prompt) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (v{p.version})
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-row-inline">
                <div className="form-group">
                  <label>Provider</label>
                  <select
                    value={newRunProvider}
                    onChange={(e) => {
                      setNewRunProvider(e.target.value);
                      // Set default model name for the provider key
                      if (e.target.value === 'claude-haiku-4-5') setNewRunModel('claude-haiku-4-5');
                      else if (e.target.value === 'claude-sonnet-4-5') setNewRunModel('claude-sonnet-4-5');
                      else if (e.target.value === 'claude-opus-4-6') setNewRunModel('claude-opus-4-6');
                      else if (e.target.value === 'gpt-4.1') setNewRunModel('gpt-4.1');
                      else if (e.target.value === 'gpt-5') setNewRunModel('gpt-5');
                      else if (e.target.value === 'gpt-5-mini') setNewRunModel('gpt-5-mini');
                      else if (e.target.value === 'gemini-2.5-pro') setNewRunModel('gemini-2.5-pro');
                      else if (e.target.value === 'gemini-2.5-flash') setNewRunModel('gemini-2.5-flash');
                      else if (e.target.value === 'gemini-2.5-flash-lite') setNewRunModel('gemini-2.5-flash-lite');
                    }}
                  >
                    <option value="claude-haiku-4-5">Claude Haiku 4.5</option>
                    <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
                    <option value="claude-opus-4-6">Claude Opus 4.6</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                    <option value="gpt-5">GPT-5</option>
                    <option value="gpt-5-mini">GPT-5 Mini</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                    <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Model</label>
                  <input type="text" value={newRunModel} onChange={(e) => setNewRunModel(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowNewRunModal(false)}>
                Cancel
              </button>
              <button
                className="start-btn"
                onClick={handleStartRun}
                disabled={createRunMutation.isPending || !newRunDatasetId || !newRunPromptId}
              >
                {createRunMutation.isPending ? 'Running...' : 'Start Run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
